// ===========================================
// Piece Completion Chart Component
// ===========================================
// Bar chart showing piece completion rates

import { Card, CardContent, CardHeader, Typography, Box, Divider, CircularProgress } from '@mui/material';
import { MusicNote as MusicIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from 'recharts';

import { StudentPieceCompletion } from '../../api/reports.api';

// ===========================================
// TYPES
// ===========================================

interface PieceCompletionChartProps {
  data: StudentPieceCompletion[];
  loading?: boolean;
}

// ===========================================
// CUSTOM TOOLTIP
// ===========================================

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  return (
    <Card sx={{ p: 1.5, minWidth: 200 }}>
      <Typography variant="body2" fontWeight={600} gutterBottom>
        {label}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Assigned:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.totalAssigned}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Completed:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.totalCompleted}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Completion Rate:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.completionRate.toFixed(1)}%
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Avg Days:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.averageDaysToComplete.toFixed(0)} days
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}

// ===========================================
// COMPONENT
// ===========================================

export default function PieceCompletionChart({ data, loading }: PieceCompletionChartProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MusicIcon color="primary" />
              <Typography variant="h6">Piece Completion Rates</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MusicIcon color="primary" />
              <Typography variant="h6">Piece Completion Rates</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No piece completion data available
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Sort by completion rate descending, take top 10
  const topPieces = [...data]
    .sort((a, b) => b.completionRate - a.completionRate)
    .slice(0, 10);

  // Generate colors based on completion rate (green for high, red for low)
  const getColor = (completionRate: number) => {
    if (completionRate >= 80) return '#4caf50'; // Green
    if (completionRate >= 60) return '#FFCE00'; // Yellow
    if (completionRate >= 40) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MusicIcon color="primary" />
            <Typography variant="h6">Piece Completion Rates</Typography>
          </Box>
        }
        subheader={`Top ${topPieces.length} pieces by completion rate`}
      />
      <Divider />
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={topPieces} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              type="number"
              domain={[0, 100]}
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="pieceName"
              width={150}
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="completionRate" name="Completion Rate (%)" radius={[0, 4, 4, 0]}>
              {topPieces.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getColor(entry.completionRate)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
